/* eslint-disable @typescript-eslint/prefer-regexp-exec */
import URLUtils from '../URLUtils';
import params from './param';
import { SystemCodeTypeEnum } from './types';

const ua = navigator.userAgent;

const versionMatch = /version=([\d.]*)/.exec(ua);
export const appVersion = (versionMatch && versionMatch[1]) || '0.0.1';

/**
 * platform详见： http://wiki.hellobike.com/pages/viewpage.action?pageId=379776923
 */
export const codeEnums: Dictionary<number> = {
  browser: 1,
  weixin: 2,
  alipay: 3,
  app: 4,
  third_party_app: 5,
  qq: 6,
  weibo: 7,
  hellotaxi_driver_app: 8,
  alipayMini: 9,
  wechatMini: 18,
  /** 钉钉浏览器 H5 */
  DingH5: 12,
  /** 臻有钱独立 app */
  FinanceApp: 13,
  /** 哈臻快app */
  rechargeApp: 16,
  /** 抖音小程序 */
  douyinMini: 17,
  /** 记账app */
  skzhenxhApp: 20,
};

const platform = {
  isWeixin: /micromessenger/i.test(ua),
  isQQ: ua.match(/QQ\/([\d.]+)/),
  isQZ: ua.indexOf('Qzone/') !== -1,
  isAndroid: /android/i.test(ua),
  isAliMP: ua.indexOf('AlipayClient') > -1 && ua.indexOf('MiniProgram') > -1,
  isAliMPMonitor:
    ua.indexOf('AlipayClient') > -1 && ua.indexOf('AlipayIDE') > -1,
  isWeibo: ua.indexOf('Weibo') > -1,
  isEasyBike: /app=easybike/.test(ua),
  isIOS: /iPad|iPhone|iPod/.test(ua),
  isHelloTaxiDriverApp: ua.match(
    /\b\s*app=HelloTaxiDriver(?:;\s*version=([\d.]+))*/i,
  ),
  /** 钉钉移动端浏览器webview H5 */
  isDingTalk: /DingTalk/.test(ua) || URLUtils.getQueryMap().dingtalk === 'true',
  /** CtripFinance 携程金融 app ua ，携程app CtripWireless ,统一识别小写的 ctrip */
  isCtripFinance: /ctrip/.test(ua.toLowerCase()) || URLUtils.getQueryMap().CtripFinance === 'true',
  /** 臻有钱独立 app */
  isFinanceApp: /skapp/.test(ua.toLowerCase()) || URLUtils.getQueryMap().skApp === 'true',
  /** 哈臻快app：在独立 app兼容的基础上进行改造 */
  isRechargeApp: /skrechargeapp/.test(ua.toLowerCase()) || URLUtils.getQueryMap().skRechargeApp === 'true',
  /** 记账app: 在哈臻快的基础上进行改造 */
  isSkzhenxhApp: /skzhenxh/.test(ua.toLowerCase()) || URLUtils.getQueryMap().skZhenxhApp === 'true',

  /** 哈啰 app 鸿蒙系统 */
  isHarmony: /OpenHarmony/i.test(ua),
  /** 微信小程序 */
  isWechatMini: /MicroMessenger[\s\S]+miniProgram/i.test(ua) || /miniProgram[\s\S]+MicroMessenger/i.test(ua),
  /** 抖音小程序 */
  isDouyinMini: /ToutiaoMicroApp/i.test(ua),
  // 判断是否是支付包小程序环境
  get isAlipayMini(): boolean {
    return this.isAliMP || this.isAliMPMonitor;
  },

  get isAlipayBrowser(): boolean {
    return !this.isAliMPMonitor && !this.isAliMP && /AlipayClient/i.test(ua);
  },

  // 哈啰系app 包含easybike、独立app、哈臻快app
  get isHelloApp(): boolean {
    return this.isEasyBike || this.isFinanceApp || this.isRechargeApp || this.isSkzhenxhApp;
  },

  /** 数科app：包含独立app、哈臻快app */
  get isSkApp(): boolean {
    return this.isFinanceApp || this.isRechargeApp || this.isSkzhenxhApp;
  },

  // 哈啰系 安卓 app
  get isHelloAndroidApp(): boolean {
    return this.isHelloApp && this.isAndroid;
  },

  // 哈啰系 iOS app
  get isHelloIosApp(): boolean {
    return this.isHelloApp && this.isIOS;
  },

  // 数科app iOS端
  get isIosSkApp(): boolean {
    return this.isSkApp && this.isIOS;
  },

  // 哈臻快app iOS端
  get isIosRechargeApp(): boolean {
    return this.isRechargeApp && this.isIOS;
  },

  get isThirdPartyAliMini(): boolean {
    const otherMini = (
      params.get('otherMini')
      || params.get('othermini')
    ) as string;

    return otherMini === 'yes' && (this.isAliMP || this.isAliMPMonitor);
  },

  get code(): number {
    return codeEnums[this.name] || 1;
  },

  get name(): string {
    if (this.isWechatMini) return 'wechatMini';
    if (this.isWeixin) return 'weixin';
    if (this.isQQ) return 'qq';
    if (this.isWeibo) return 'weibo';
    if (this.isEasyBike) return 'app';
    if (this.isQZ) return 'qzone';
    if (this.isAlipayBrowser) return 'alipay';
    if (this.isAliMP || this.isAliMPMonitor) return 'alipayMini';
    if (this.isHelloTaxiDriverApp) return 'hellotaxi_driver_app';
    if (this.isDingTalk) return 'DingH5';
    if (this.isFinanceApp) return 'FinanceApp';
    if (this.isRechargeApp) return 'rechargeApp';
    if (this.isSkzhenxhApp) return 'skzhenxhApp';
    if (this.isDouyinMini) return 'douyinMini';
    return 'browser';
  },

  // 钉钉场景 flowSource值
  get DingTalkFlowSource(): string {
    return '60012';
  },

  // 携程金融webview h5活体 flowSource值
  // get CtripFinanceFlowSource(): string {
  //   return '62002';
  // },

  /**
   * 是否是iphonex以及iPhoneX系列
   */
  get isIPhoneX(): boolean {
    // iPhone X、iPhone XS
    return !!(
      /iphone/gi.test(window.navigator.userAgent)
      && window.devicePixelRatio
      && window.devicePixelRatio === 3
      && window.screen.width === 375
      && window.screen.height === 812
    );
  },

  get isIPhoneXSMax(): boolean {
    // iPhone XS Max
    return !!(
      /iphone/gi.test(window.navigator.userAgent)
      && window.devicePixelRatio
      && window.devicePixelRatio === 3
      && window.screen.width === 414
      && window.screen.height === 896
    );
  },

  get isIPhoneXR(): boolean {
    // iPhone XR
    return !!(
      /iphone/gi.test(window.navigator.userAgent)
      && window.devicePixelRatio
      && window.devicePixelRatio === 2
      && window.screen.width === 414
      && window.screen.height === 896
    );
  },

  // 荣耀机型
  get isHonor(): boolean {
    return !!(/honor/gi.test(window.navigator.userAgent));
  },

  get isIPhoneXSeries(): boolean {
    return !!(this.isIPhoneX || this.isIPhoneXSMax || this.isIPhoneXR);
  },

  // 获取当前环境类型
  get currentPlatformType(): {platform: string} {
    return { platform: String(this.code) };
  },

  // 当前平台编号
  get systemCode(): string {
    if (this.isAlipayMini) {
      return SystemCodeTypeEnum.Alipay;
    } if (this.isEasyBike) {
      if (this.isAndroid) {
        return SystemCodeTypeEnum.AppAndroid;
      }
      if (this.isHarmony) {
        return SystemCodeTypeEnum.Harmony;
      }
      return SystemCodeTypeEnum.AppIos;
    } if (this.isFinanceApp) {
      return this.isAndroid ? SystemCodeTypeEnum.Zyq02 : SystemCodeTypeEnum.Zyq01;
    } if (this.isRechargeApp) {
      return this.isAndroid ? SystemCodeTypeEnum.Zxh02 : SystemCodeTypeEnum.Zxh01;
    } if (this.isSkzhenxhApp) {
      return this.isAndroid ? SystemCodeTypeEnum.Zxy02 : SystemCodeTypeEnum.Zxy01;
    } if (this.isWechatMini) {
      return SystemCodeTypeEnum.Zyq04;
    }

    return SystemCodeTypeEnum.H5;
  },
};

export default platform;
